import { PayloadActionCreator, createAction } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import {
  ClientService,
  ResponseBalances,
  ResponseShop,
} from '@ads/front-core/api'
import { Balances } from '@ads/front-core/interfaces'
import { getIsDemo } from '@ads/front-core/store/selectors/getIsDemo'
import { getCurrentShop } from 'store/selectors/getCurrentShop'
import { setBalancesLoading } from 'store/reducers/balances/setBalancesLoadingReducer'
import { setBalances } from 'store/reducers/balances/setBalancesReducer'
import { hideBanner } from '@ads/front-core/store/reducers/banner/hideBannerReducer'
import { banner } from '@ads/front-core/store/sagas/bannerSaga'
import { lng } from '@ads/front-core/locales'

const SHOP_OVERLIMIT_BANNER_ID = 'SHOP_OVERLIMIT_BANNER_ID'

interface FetchBalanceProps {
  shopId: number
}

type FetchBalanceSaga = ReturnType<
  PayloadActionCreator<FetchBalanceProps, string>
>

export function* sagaFetchBalance(): Generator {
  yield takeEvery(fetchBalance.type, fetchBalanceSaga)
}

export const fetchBalance = createAction<FetchBalanceProps>('saga/fetchBalance')

export function* fetchBalanceSaga({ payload }: FetchBalanceSaga): Generator {
  const { shopId } = payload

  if (!shopId) {
    return
  }

  const isDemo = yield select(getIsDemo)

  if (isDemo) {
    yield put(
      setBalances({
        client: {
          balance: 9970,
          blockedFunds: 200,
          monthLimit: 5000,
          monthlyBalance: 6000,
          isBalanceOk: true,
        },
        shop: {
          balance: 9970,
          blockedFunds: 200,
          monthLimit: 5000,
          monthlyBalance: 6000,
          isBalanceOk: true,
        },
        isLoading: false,
      }),
    )
    return
  }

  yield put(setBalancesLoading(true))

  const shop = (yield select(getCurrentShop)) as ResponseShop

  try {
    const res = yield call(ClientService.getBalances, {
      shopId,
    })

    const responseBalance = res as ResponseBalances

    const balance: Balances = {
      client: {
        blockedFunds: responseBalance?.client?.blockedFunds || 0,
        monthLimit: responseBalance?.client?.monthLimit || 0,
        monthlyBalance: responseBalance?.client?.monthlyBalance || 0,
        balance: responseBalance?.client?.balance || 0,
        isBalanceOk: Boolean(responseBalance?.client?.isBalanceOk),
      },
      shop: {
        blockedFunds: responseBalance?.shop?.blockedFunds || 0,
        monthLimit: responseBalance?.shop?.monthLimit || 0,
        monthlyBalance: responseBalance?.shop?.monthlyBalance || 0,
        balance: responseBalance?.shop?.balance || 0,
        isBalanceOk: Boolean(responseBalance?.shop?.isBalanceOk),
      },
      isLoading: false,
    }

    if (balance.shop?.isBalanceOk === false && shop?.monthLimit > 0) {
      yield put(
        banner({
          id: SHOP_OVERLIMIT_BANNER_ID,
          description: lng('common:shopLimitOverrunWarning'),
          variant: 'error',
          disableAutoHide: true,
        }),
      )
    } else {
      yield put(
        hideBanner({
          id: SHOP_OVERLIMIT_BANNER_ID,
        }),
      )
    }

    yield put(setBalances(balance))
  } catch (error) {
    //
  }

  yield put(setBalancesLoading(false))
}
